import React from 'react';
import { Sparkles } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button } from '@/components/ui';
import { useUserStore } from '../../../store/userStore';
import { usePenpal } from '../../../hooks/usePenpal';

interface PremiumBrandingAlertProps {
  context?: 'guide' | 'help-center';
}

export const PremiumBrandingAlert: React.FC<PremiumBrandingAlertProps> = ({ 
  context = 'guide' 
}) => {
  const isHelpCenter = context === 'help-center';
  const { account } = useUserStore();
  const { openExternalUrl } = usePenpal();

  const handleUpgradeClick = () => {
    const url =
      "https://folge.me/go/cloud?cloudemail=" +
      encodeURIComponent(account?.email || "") +
      "#pricing";
    openExternalUrl(url);
  };
  
  return (
    <Alert 
      className="relative overflow-hidden border-0 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/20 dark:via-yellow-950/20 dark:to-orange-950/20 shadow-sm"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 via-yellow-400/5 to-orange-400/10 pointer-events-none" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-200/20 to-transparent rounded-full blur-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-yellow-200/20 to-transparent rounded-full blur-xl pointer-events-none" />
      
      <div className="relative flex items-start gap-4">
        {/* Icon with gradient background */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 shadow-md">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <AlertTitle className="text-base font-semibold text-amber-900 dark:text-amber-100 mb-1.5">
            Premium Feature
          </AlertTitle>
          <div className="flex items-center gap-3 flex-wrap">
            <AlertDescription className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed flex-1 min-w-0">
              {isHelpCenter ? (
                <>
                  Custom branding (logo, colors) is available for <strong>Pro Cloud</strong> plans. 
                  Upgrade to unlock this feature and customize your help center's appearance.
                </>
              ) : (
                <>
                  Custom branding (logo, colors) is available for <strong>Pro Cloud</strong> plans. 
                  Upgrade to unlock this feature and customize how your guides appear.
                </>
              )}
            </AlertDescription>
            <Button
              onClick={handleUpgradeClick}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white shrink-0"
              size="sm"
            >
              Upgrade Plan
            </Button>
          </div>
        </div>
      </div>
    </Alert>
  );
};

